import { create } from 'zustand';
import { getDeviceZone } from '../utils/timezone';

// Tracks the zone the user is currently logging in. Defaults to the device's
// auto-detected zone; the user can override it (persisted in localStorage), and
// reset back to auto-detect. Every start time logged is stamped with this zone,
// and Quick Adds are converted into it.

const OVERRIDE_KEY = 'mytime.timezone.override';

const loadOverride = (): string | null => {
  try {
    return localStorage.getItem(OVERRIDE_KEY) || null;
  } catch {
    return null;
  }
};

interface TimezoneState {
  deviceZone: string; // auto-detected device zone
  overrideZone: string | null; // user-chosen override, or null for auto
  currentZone: string; // effective zone (override ?? device)
  isAuto: boolean;
  setOverride: (zone: string) => void;
  resetToDevice: () => void;
  refreshDeviceZone: () => void;
}

const initialDevice = getDeviceZone();
const initialOverride = loadOverride();

export const useTimezoneStore = create<TimezoneState>((set, get) => ({
  deviceZone: initialDevice,
  overrideZone: initialOverride,
  currentZone: initialOverride || initialDevice,
  isAuto: !initialOverride,

  setOverride: (zone: string) => {
    try {
      localStorage.setItem(OVERRIDE_KEY, zone);
    } catch {
      /* localStorage unavailable — keep in memory only */
    }
    set({ overrideZone: zone, currentZone: zone, isAuto: false });
  },

  resetToDevice: () => {
    try {
      localStorage.removeItem(OVERRIDE_KEY);
    } catch {
      /* noop */
    }
    const device = getDeviceZone();
    set({ deviceZone: device, overrideZone: null, currentZone: device, isAuto: true });
  },

  refreshDeviceZone: () => {
    const device = getDeviceZone();
    const { overrideZone } = get();
    set({
      deviceZone: device,
      currentZone: overrideZone || device,
      isAuto: !overrideZone,
    });
  },
}));
